import React, { useEffect } from "react";
import { RootStateOrAny, useSelector } from "react-redux";
import { Spin } from "antd";
import { FormFieldValue } from "../../lib/interfaces/FormFieldValue";
import {
  accountsPageableSearch,
  getAccountsOrganization,
  getAccountVersions,
  getOrganizationVersions,
  getSubscribersVersions,
} from "../../store/slices/accountsTs";
import { checkIsLoaded, checkIsLoading } from "../../lib/utils/checkLoadingStatus";
import AccountListTs from "../../components/AccountListTs/AccountListTs";
import {
  fetchAccountStatuses,
  fetchCountries,
  fetchRegions,
  fetchAccountTypes,
  getOrganizationDicVersions,
} from "../../store/slices/dicts";
import { useAppDispatch, useAppSelector } from "../../index";
import LocalStorageService from "../../services/LocalStorageService";
import usePhoneMask from "../../lib/hooks/usePhoneMask";

interface ISearchParams {
  fullName?: string;
  shortName?: string;
  propertyTypeId?: string;
  propertyStringValue?: string;
  externalId?: string;
  agreementNumber?: string;
  agreementId?: string;
  accountStatusId?: string;
  billDeliveryEmail?: string;
  page: number;
  id?: string;
  phone?: string;
}

interface IAccountListContainerTs {
  advancedSearchFormValues: FormFieldValue[];
  setAdvancedSearchFormValues: React.Dispatch<React.SetStateAction<FormFieldValue[] | undefined>>;
  searchByNameFields: FormFieldValue[];
  isPageableSearch: boolean;
  setIsPageableSearch: React.Dispatch<React.SetStateAction<boolean>>;
  setSearchByNameFields: React.Dispatch<React.SetStateAction<FormFieldValue[] | undefined>>;
}

function AccountsListContainerTs({ routeProps }: { routeProps: IAccountListContainerTs }) {
  const {
    advancedSearchFormValues,
    setAdvancedSearchFormValues,
    searchByNameFields,
    setSearchByNameFields,
    isPageableSearch,
    setIsPageableSearch,
  } = routeProps;

  const dispatch = useAppDispatch();
  const {
    accountStatusesLoading,
    regionsLoading,
    countriesLoading,
    accountTypesLoading,
  } = useAppSelector((state) => state.dicts);
  const { cleanFullPhoneNumber } = usePhoneMask();

  const handlePageableSearch = (values: FormFieldValue[]) => {
    const searchParams: ISearchParams = {
      accountStatusId: undefined,
      agreementNumber: undefined,
      billDeliveryEmail: undefined,
      externalId: undefined,
      fullName: undefined,
      page: 1,
      shortName: undefined,
      propertyStringValue: undefined,
      propertyTypeId: undefined,
      id: undefined,
      phone: undefined,
    };

    const formatIdString = (ids: string) => ids.replace(/\D/g, " ").replace(/\s+/g, ",");

    values.forEach((item) => {
      if (item.value && (item.name === "id" || item.name === "agreementId") && item.value !== "") {
        searchParams[item.name] = formatIdString(item.value);
      } else if (item.value && item.name === "phone") {
        searchParams.phone = cleanFullPhoneNumber(item.value);
      } else if (item.value && item.name === "propertyStringValue" && item.value !== "") {
        searchParams.propertyTypeId = LocalStorageService.getTenantId() === "1" ? "BIN" : "INN";
        searchParams.propertyStringValue = item.value;
      } else {
        searchParams[item.name] = item.value;
      }
    });

    dispatch(
      accountsPageableSearch({
        ...searchParams,
      })
    );
  };

  useEffect(() => {
    dispatch(getAccountsOrganization({ page: 0 }));
  }, []);

  useEffect(() => {
    if (!checkIsLoaded(accountStatusesLoading)) dispatch(fetchAccountStatuses());
    if (!checkIsLoaded(regionsLoading)) dispatch(fetchRegions());
    if (!checkIsLoaded(countriesLoading)) dispatch(fetchCountries());
    if (!checkIsLoaded(accountTypesLoading)) dispatch(fetchAccountTypes());
  }, []);

  const getRowVersions = (params: {
    accountId: number;
    organizationId: number;
    subscribersId: number[];
  }) => {
    const { accountId, organizationId, subscribersId } = params;
    dispatch(getAccountVersions({ accountId }));
    dispatch(getOrganizationVersions({ organizationId }));
    dispatch(getSubscribersVersions({ subscribersId }));
    dispatch(getOrganizationDicVersions({ organizationId }));
  };

  return (
    <div className="container_height">
      <Spin className="container_height" spinning={checkIsLoading(accountStatusesLoading)}>
        {checkIsLoaded(accountStatusesLoading) && (
          <AccountListTs
            getRowVersions={getRowVersions}
            handlePageableSearch={handlePageableSearch}
            isPageableSearch={isPageableSearch}
            advancedSearchFormValues={advancedSearchFormValues}
            setAdvancedSearchFormValues={setAdvancedSearchFormValues}
            setIsPageableSearch={setIsPageableSearch}
            searchByNameFields={searchByNameFields}
            setSearchByNameFields={setSearchByNameFields}
          />
        )}
      </Spin>
    </div>
  );
}

export default AccountsListContainerTs;
